import { CtaLesson } from 'components/cta-lesson'
import { Layout } from 'components/layout'
import { LESSON_NAMES } from 'constants/lessons'
import { PAGES } from 'constants/routes'
import { useLoadInitialData } from 'hooks/database/use-load-initial-data'
import { useAppSelector } from 'hooks/use-app-selector'
import Link from 'next/link'
import { useRouter } from 'next/router'
import { useAuthUser } from 'next-firebase-auth'
import { selectIsAiSubscriptionActiveAndNotAdFreeOnly } from 'store/slices/subscription/subscription'
import { Button } from 'ui-kit/button'
import { urlWithSiteOrigin } from 'utils/url-with-site-origin'

import { KeyboardIcon } from './icons'
import { features } from './resources'
import {
  CoachPageWrapper,
  ContentContainer,
  CtaContainer,
  FeatureCard,
  FeaturesGrid,
  FooterCta,
  HeroContent,
  HeroSection,
} from './typing-mentor-coach.styles'

export const TypingMentorCoach = () => {
  useLoadInitialData()
  const isAiSubscriber = useAppSelector(selectIsAiSubscriptionActiveAndNotAdFreeOnly)

  const authUser = useAuthUser()

  const router = useRouter()

  const handleSubscribe = () => {
    router.push(PAGES['/pricing/'])
  }

  const GoToAiLessonsCta = ({ text }: { text: string }) => {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
        <CtaLesson
          cta={LESSON_NAMES.AI}
          href={PAGES['/typing-practice/']}
          title="Go to AI lessons practice page"
          buttonView
          blank={false}
        >
          {text}
        </CtaLesson>
      </div>
    )
  }

  const SubscribeNowCta = ({ text }: { text: string }) => {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
        <Button onClick={handleSubscribe}>{text}</Button>
      </div>
    )
  }

  return (
    <Layout
      signOut={authUser.signOut}
      seoProps={{
        title: 'TypingMentor AI Coach - Your Personalized Typing Assistant',
        description:
          'Elevate your typing skills with TypingMentor AI Coach. Get personalized feedback, adaptive lessons, and real-time progress tracking to master touch typing faster than ever.',
        canonicalUrl: urlWithSiteOrigin('/typing-mentor-coach/'),
        pageUrl: urlWithSiteOrigin('/typing-mentor-coach/'),
      }}
    >
      <CoachPageWrapper>
        {/* Hero Section */}
        <HeroSection>
          <HeroContent>
            <h1>TypingMentor AI Coach - Your Personalized Typing Assistant</h1>
            <p>
              Master touch typing faster and more effectively with TypingMentor AI Coach. Our advanced AI-powered
              platform provides personalized guidance, real-time feedback, and adaptive lessons tailored to your unique
              learning style.
            </p>
          </HeroContent>
        </HeroSection>

        {/* CTA Section */}
        <ContentContainer>
          <CtaContainer>
            {isAiSubscriber ? (
              <>
                <p>
                  You have full access to the AI Coach feature. Start practicing with AI-generated lessons tailored to
                  your needs.
                </p>
                <GoToAiLessonsCta text="🚀 Go to Typing Practice Page" />
              </>
            ) : (
              <>
                <p>
                  Ready to transform your typing skills? Subscribe Now &nbsp;and experience the future of typing
                  education.
                </p>
                <SubscribeNowCta text="🚀 Subscribe Now" />
              </>
            )}
            <p style={{ textAlign: 'center', marginTop: '2rem' }}>
              Need Help?
              <Link title="Go to contact us page" style={{ margin: '-50px' }} href={PAGES['/contact-us/']}>
                Contact Us
              </Link>
            </p>
          </CtaContainer>

          <h2 style={{ textAlign: 'center', marginTop: '3rem' }}>Why Choose TypingMentor AI Coach?</h2>

          {/* Features Grid */}
          <FeaturesGrid>
            {features.map((feature, index) => (
              <FeatureCard key={index}>
                <h2>
                  {feature.icon} {feature.title}
                </h2>
                <p>{feature.description}</p>
              </FeatureCard>
            ))}
          </FeaturesGrid>

          {/* Footer CTA */}
          <FooterCta>
            <h2 style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
              {KeyboardIcon}
              <span>Ready to transform your typing skills?</span>
            </h2>

            {isAiSubscriber ? (
              <>
                <p style={{ fontSize: '1.2rem' }}>
                  You have full access to the AI Coach feature. Start practicing with AI-generated lessons tailored to
                  your needs.
                </p>
                <GoToAiLessonsCta text="🚀 Go to Typing Practice Page" />
              </>
            ) : (
              <>
                <p style={{ fontSize: '1.2rem' }}>
                  Subscribe Now and experience the future of typing education with TypingMentor AI Coach.
                </p>
                <SubscribeNowCta text="🚀 Subscribe Now" />
              </>
            )}
          </FooterCta>
        </ContentContainer>
      </CoachPageWrapper>
    </Layout>
  )
}
